import { useEffect, useState } from "react";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { checkWatchlist, addWatchlist, removeWatchlist, listWatchlist } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Shared cache across every rendered WatchStoreButton so we don't fire N /check
// requests when a home feed renders 60+ cards. Populated once per session.
let _watchCache = null;      // Set<string> of stores the user is tracking, or null if not loaded
let _watchPromise = null;    // in-flight fetch promise for de-dup
const _listeners = new Set();

async function _loadWatchCache() {
  if (_watchCache) return _watchCache;
  if (_watchPromise) return _watchPromise;
  _watchPromise = listWatchlist()
    .then((list) => {
      _watchCache = new Set((list || []).map((w) => w.store));
      _watchPromise = null;
      return _watchCache;
    })
    .catch(() => {
      _watchCache = new Set();
      _watchPromise = null;
      return _watchCache;
    });
  return _watchPromise;
}

function _notify() {
  for (const l of _listeners) l();
}

export function invalidateWatchCache() {
  _watchCache = null;
  _watchPromise = null;
  _notify();
}

/**
 * Toggle tracking for a store. Fires a price-drop email when new deals ingest for this store.
 */
export default function WatchStoreButton({ store, variant = "default" }) {
  const { user, openLogin } = useAuth();
  const [tracking, setTracking] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || !store) {
      setTracking(false);
      return;
    }
    let alive = true;
    _loadWatchCache().then((set) => {
      if (alive) setTracking(set.has(store));
    });
    const onChange = () => {
      if (alive && _watchCache) setTracking(_watchCache.has(store));
    };
    _listeners.add(onChange);
    return () => {
      alive = false;
      _listeners.delete(onChange);
    };
  }, [user, store]);

  const handleToggle = async (e) => {
    e?.stopPropagation?.();
    if (!user) {
      toast("Sign in to track stores", {
        action: { label: "Sign in", onClick: () => openLogin("signin") },
      });
      return;
    }
    setLoading(true);
    try {
      if (tracking) {
        await removeWatchlist(store);
        setTracking(false);
        if (_watchCache) _watchCache.delete(store);
        _notify();
        toast.success(`Stopped tracking ${store}`);
      } else {
        await addWatchlist(store);
        setTracking(true);
        if (_watchCache) _watchCache.add(store);
        _notify();
        toast.success(`Tracking ${store} — we'll email you when new deals drop`, { duration: 4000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update tracking");
    } finally {
      setLoading(false);
    }
  };

  if (variant === "compact") {
    return (
      <button
        onClick={handleToggle}
        disabled={loading}
        data-testid={`watch-store-btn-${store}`}
        aria-label={tracking ? `Stop tracking ${store}` : `Track ${store}`}
        title={tracking ? `Stop tracking ${store}` : `Track ${store} for price-drop alerts`}
        className={`w-10 h-10 border-2 border-[#2d2a32] rounded-full flex items-center justify-center transition-colors disabled:opacity-50 ${
          tracking ? "bg-[#f7c873]" : "bg-white hover:bg-[#f7c873]"
        }`}
      >
        {tracking ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
      </button>
    );
  }

  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      data-testid={`watch-store-btn-${store}`}
      className={`inline-flex items-center gap-2 h-11 px-5 border-2 border-[#2d2a32] rounded-full text-[11px] tracking-[0.22em] font-bold transition-colors disabled:opacity-50 ${
        tracking
          ? "bg-[#f7c873] text-[#2d2a32] hover:bg-[#f2a93b]"
          : "bg-white text-[#2d2a32] hover:bg-[#4ecdc4] hover:text-white"
      }`}
    >
      {tracking ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
      {loading ? "..." : tracking ? "TRACKING ✓" : "TRACK THIS STORE"}
    </button>
  );
}
